/**
 * FreePolygon — TS port of SharedCore/FreePolygon.swift。
 * 角點 (x,y)、單位公尺。Shoelace 算面積 + ray-cast 點包含。
 * 跟 iOS 卡扣地板 / 磁磚計算機 Pro 用同邏輯、確保 web/native 算同份料。
 */

export interface Point2D {
  x: number;
  y: number;
}

export interface RectM {
  x: number;
  y: number;
  width: number;
  height: number;
}

export class FreePolygon {
  constructor(public vertices: Point2D[]) {}

  /** Shoelace formula、回正值不論 winding。 */
  get areaM2(): number {
    const v = this.vertices;
    if (v.length < 3) return 0;
    let sum = 0;
    for (let i = 0; i < v.length; i++) {
      const a = v[i];
      const b = v[(i + 1) % v.length];
      sum += a.x * b.y - b.x * a.y;
    }
    return Math.abs(sum) / 2;
  }

  /** 邊長累加。 */
  get perimeterM(): number {
    const v = this.vertices;
    if (v.length < 2) return 0;
    let sum = 0;
    for (let i = 0; i < v.length; i++) {
      const a = v[i];
      const b = v[(i + 1) % v.length];
      sum += Math.hypot(b.x - a.x, b.y - a.y);
    }
    return sum;
  }

  /** 軸對齊 bounding box。 */
  get boundingBox(): RectM {
    if (!this.vertices.length) return { x: 0, y: 0, width: 0, height: 0 };
    let minX = this.vertices[0].x, minY = this.vertices[0].y;
    let maxX = minX, maxY = minY;
    for (const v of this.vertices) {
      if (v.x < minX) minX = v.x;
      if (v.y < minY) minY = v.y;
      if (v.x > maxX) maxX = v.x;
      if (v.y > maxY) maxY = v.y;
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }

  /** 個別邊長 list (順著 vertices 方向)。 */
  get edgeLengthsM(): number[] {
    const v = this.vertices;
    const out: number[] = [];
    for (let i = 0; i < v.length; i++) {
      const a = v[i];
      const b = v[(i + 1) % v.length];
      out.push(Math.hypot(b.x - a.x, b.y - a.y));
    }
    return out;
  }

  /** Ray-cast 點包含。 */
  contains(px: number, py: number): boolean {
    const v = this.vertices;
    if (v.length < 3) return false;
    let inside = false;
    let j = v.length - 1;
    for (let i = 0; i < v.length; i++) {
      const vi = v[i], vj = v[j];
      const intersects =
        ((vi.y > py) !== (vj.y > py)) &&
        (px < ((vj.x - vi.x) * (py - vi.y)) / (vj.y - vi.y) + vi.x);
      if (intersects) inside = !inside;
      j = i;
    }
    return inside;
  }

  /** 是否已閉合：頭尾距離 < 容許值（避免 WebXR 點來點去沒接好）。 */
  isClosed(toleranceM = 0.05): boolean {
    if (this.vertices.length < 3) return false;
    const first = this.vertices[0];
    const last = this.vertices[this.vertices.length - 1];
    return Math.hypot(last.x - first.x, last.y - first.y) < toleranceM;
  }

  static rectangle(widthM: number, heightM: number): FreePolygon {
    return new FreePolygon([
      { x: 0, y: 0 },
      { x: widthM, y: 0 },
      { x: widthM, y: heightM },
      { x: 0, y: heightM },
    ]);
  }
}

/**
 * 卡扣地板 / 磁磚估料 — 跟 iOS FlooringMaterialCalculator 對齊。
 * 簡化版：板數、PE 墊料、矽利康、踢腳板。
 */
export interface FlooringEstimate {
  areaM2: number;
  perimeterM: number;
  plankCount: number;       // 含 loss rate
  lossRatePct: number;
  peUnderlaymentM2: number;
  siliconeTubes: number;    // 1 條 5m
  skirtingM: number;
}

export interface FlooringSpec {
  plankLengthMm: number;
  plankWidthMm: number;
  lossRatePct: number;      // 11=11%
}

export function estimateFlooring(poly: FreePolygon, spec: FlooringSpec): FlooringEstimate {
  const area = poly.areaM2;
  const perimeter = poly.perimeterM;
  const plankAreaM2 = (spec.plankLengthMm / 1000) * (spec.plankWidthMm / 1000);
  const rawCount = area / plankAreaM2;
  const plankCount = Math.ceil(rawCount * (1 + spec.lossRatePct / 100));
  const peUnderlaymentM2 = area * 1.05;       // 5% overlap
  const siliconeTubes = Math.max(1, Math.ceil(perimeter / 5));
  return {
    areaM2: area,
    perimeterM: perimeter,
    plankCount,
    lossRatePct: spec.lossRatePct,
    peUnderlaymentM2,
    siliconeTubes,
    skirtingM: perimeter,
  };
}
